import { HistoryEntry, HistoryState } from '../types/history';
import { TravelExpense, TravelExpenseReport } from '../types/expense';

const HISTORY_STORAGE_KEY = 'travel-expense-history';
const DEFAULT_MAX_ENTRIES = 20;

export class HistoryManager {
  private static instance: HistoryManager;
  private historyState: HistoryState;

  private constructor() {
    this.historyState = this.loadHistory();
  }

  static getInstance(): HistoryManager {
    if (!HistoryManager.instance) {
      HistoryManager.instance = new HistoryManager();
    }
    return HistoryManager.instance;
  }

  private loadHistory(): HistoryState {
    try {
      const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (savedHistory) {
        return JSON.parse(savedHistory);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
    return {
      entries: [],
      maxEntries: DEFAULT_MAX_ENTRIES,
    };
  }

  private saveHistory(): void {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.historyState));
    } catch (error) {
      console.error('Error saving history:', error);
    }
  }

  addEntry(
    expenses: TravelExpense[],
    reportInfo: Partial<TravelExpenseReport>,
    description?: string
  ): void {
    const timestamp = Date.now();
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Deep clone expenses but exclude File objects (they're stored in IndexedDB)
    const clonedExpenses = expenses.map(expense => ({
      ...expense,
      receipt: undefined, // Don't store File objects in history
    }));

    const entry: HistoryEntry = {
      id: `history-${timestamp}`,
      timestamp,
      date: new Date(timestamp).toLocaleString('de-DE'),
      description: description || this.generateDescription(expenses),
      expenses: clonedExpenses,
      reportInfo: JSON.parse(JSON.stringify(reportInfo)), // Deep clone
      totalAmount,
      expenseCount: expenses.length,
    };

    this.historyState.entries.unshift(entry);

    // Keep only the most recent entries
    if (this.historyState.entries.length > this.historyState.maxEntries) {
      this.historyState.entries = this.historyState.entries.slice(0, this.historyState.maxEntries);
    }

    this.saveHistory();
  }

  private generateDescription(expenses: TravelExpense[]): string {
    if (expenses.length === 0) return 'Leere Ausgabenliste';
    if (expenses.length === 1) return `1 Ausgabe (${expenses[0].description})`;
    return `${expenses.length} Ausgaben`;
  }

  getHistory(): HistoryEntry[] {
    return this.historyState.entries;
  }

  getEntry(id: string): HistoryEntry | undefined {
    return this.historyState.entries.find(entry => entry.id === id);
  }

  deleteEntry(id: string): void {
    this.historyState.entries = this.historyState.entries.filter(entry => entry.id !== id);
    this.saveHistory();
  }

  clearHistory(): void {
    this.historyState.entries = [];
    this.saveHistory();
  }

  setMaxEntries(maxEntries: number): void {
    this.historyState.maxEntries = maxEntries;
    if (this.historyState.entries.length > maxEntries) {
      this.historyState.entries = this.historyState.entries.slice(0, maxEntries);
    }
    this.saveHistory();
  }
}
